/* eslint-disable react/no-unknown-property */
import { useDeferredValue, useMemo } from 'react';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Edges, Text } from '@react-three/drei';
import { getBoxMeshData } from './BoxMesh';

const UNIT_DIVISOR = 2;

// packageFilter defaults to 'ALL' (no filtering) — only Beyond CBM's package
// dropdown ever passes a specific item id, so console's rendering is
// unaffected by construction, not by a branch. highlighted defaults to
// false for the same reason — only Beyond CBM's legend hover ever sets it.
const HandStuffedContainer = ({ container, entities, zValue, animationProgress, edgeColor = 'red', packageFilter = 'ALL', highlighted = false }) => {
  const containerDetails = useMemo(() => {
    const key = Object.keys(entities.containers).find((k) => k === container.id);
    return key ? entities.containers[key] : null;
  }, [container, entities]);

  const containerHeight = (containerDetails?.height || 0) / UNIT_DIVISOR;
  const containerWidth = (containerDetails?.width || 0) / UNIT_DIVISOR;
  const containerLength = (containerDetails?.length || 0) / UNIT_DIVISOR;

  const deferredProgress = useDeferredValue(animationProgress);

  const mergedBoxes = useMemo(() => {
    let boxIndex = 1;
    const geometries = [];

    (container.stacks || []).forEach((stackArray) => {
      if (!stackArray.length) return;
      const stack = stackArray[0];
      const itemKey = Object.keys(entities.items).find((k) => k === stack.item);
      if (!itemKey) return;

      const itemDetails = entities.items[itemKey];
      const getDim = (side) => {
        const sideVal = stack.orientation[side];
        if (sideVal === 'length') return itemDetails.length;
        if (sideVal === 'width') return itemDetails.width;
        if (sideVal === 'height') return itemDetails.height;
        return 0;
      };

      const itemLength = getDim('length');
      const itemHeight = getDim('height');
      const itemWidth = getDim('width');

      for (let i = 0; i < stack.quantity; i++) {
        // boxIndex still increments regardless of the filter — it drives the
        // animation reveal count, which must stay identical whether or not
        // a package filter is narrowing what's actually drawn.
        if (boxIndex <= deferredProgress && (packageFilter === 'ALL' || stack.item === packageFilter)) {
          const { geometry } = getBoxMeshData({
            dimensions: { length: itemLength, height: itemHeight, width: itemWidth },
            coordinates: [
              stack.coordinates[0],
              stack.coordinates[1],
              stack.coordinates[2] + (i + 1) * (itemHeight / UNIT_DIVISOR),
            ],
            color: itemDetails?.color || 'blue',
          });
          geometries.push(geometry);
        }
        boxIndex++;
      }
    });

    if (!geometries.length) return null;
    const mergedGeometry = mergeGeometries(geometries, false);
    const material = new THREE.MeshBasicMaterial({ vertexColors: true });
    return { geometry: mergedGeometry, material };
  }, [container, entities, deferredProgress, packageFilter]);

  if (!containerDetails) return null;

  return (
    <group position={[0, 0, -zValue]}>
      <mesh position={[containerLength / 2, containerHeight / 2, containerWidth / 2]}>
        <boxGeometry args={[containerLength, containerHeight, containerWidth]} />
        <meshStandardMaterial transparent opacity={0.01} />
        <Edges linewidth={highlighted ? 2.5 : 1} threshold={15} color={highlighted ? '#fbbf24' : edgeColor} />
      </mesh>

      <group
        position={[containerLength + 0.2, containerHeight / 2, containerWidth / 2]}
        rotation={[0, Math.PI / 2, 0]}
      >
        <mesh>
          <planeGeometry args={[containerHeight / UNIT_DIVISOR, containerWidth / UNIT_DIVISOR]} />
          <meshBasicMaterial color="black" side={THREE.DoubleSide} />
        </mesh>
        {/* Explicit local font — without one, troika-three-text falls back to
            its unicode-font-resolver, which fetches from a jsdelivr CDN at
            runtime. react-snap's skipThirdPartyRequests blocks that fetch
            during prerendering, and troika doesn't catch the failure — it
            throws, which crashes the whole prerender build (discovered when
            /examples, the first route with populated 3D content, was added
            to the prerender list). Same-origin font file sidesteps the CDN
            entirely, so this works with zero external network access. */}
        <Text
          position={[0, 0, 0.01]}
          font="/fonts/SpaceGrotesk-Bold.ttf"
          fontSize={(containerHeight / UNIT_DIVISOR) * 0.1}
          maxWidth={(containerHeight / UNIT_DIVISOR) * 0.9}
          lineHeight={1.6}
          textAlign="center"
          color="white"
          anchorX="center"
          anchorY="middle"
        >
          {`${container.id}\n(x${container.quantity})`}
        </Text>
      </group>

      <mesh position={[containerLength / 2, containerHeight / 2, containerWidth / 2]}>
        <boxGeometry args={[containerLength, containerHeight, containerWidth]} />
        <meshBasicMaterial color={highlighted ? '#fbbf24' : 0xff9999} transparent opacity={highlighted ? 0.22 : 0.08} side={THREE.DoubleSide} />
      </mesh>

      {mergedBoxes && (
        <>
          <mesh geometry={mergedBoxes.geometry} material={mergedBoxes.material} />
          <lineSegments
            geometry={new THREE.EdgesGeometry(mergedBoxes.geometry)}
            material={new THREE.LineBasicMaterial({ color: 'black' })}
          />
        </>
      )}
    </group>
  );
};

export default HandStuffedContainer;
